import { faSend } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, TextInput } from '@patternfly/react-core';
import { useState, useEffect, useRef } from 'react';

interface Message {
	id: number;
	text: string;
	isUser: boolean;
	sender: 'user' | 'bot';
}

const ChatInterface = () => {
	const [messages, setMessages] = useState<Message[]>([]);
	const [input, setInput] = useState('');
	const [isThinking, setIsThinking] = useState(false);
	const [showFloatingChat, setShowFloatingChat] = useState(false);

	const messagesEndRef = useRef<HTMLDivElement | null>(null);
	const chatContainerRef = useRef<HTMLDivElement | null>(null);

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
		if (chatContainerRef.current) {
			chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
		}
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages, isThinking]);

	const handleSend = () => {
		if (!input.trim() || isThinking) return;

		const userMessage: Message = {
			id: Date.now(),
			text: input,
			isUser: true,
			sender: 'user',
		};

		setMessages((prev) => [...prev, userMessage]);
		setInput('');
		setIsThinking(true);

		// Simulate AI thinking for 4 seconds
		setTimeout(() => {
			const aiMessage: Message = {
				id: Date.now() + 1,
				text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
				isUser: false,
				sender: 'bot',
			};
			setMessages((prev) => [...prev, aiMessage]);
			setIsThinking(false);
		}, 4000);
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	return (
		<div className="chat-container">
			<small>
				<FontAwesomeIcon icon={faInfoCircle} /> Follow up question? Prompt to get further
				insight...
				<br />
				<br />
			</small>

			<div
				className="chat-messages"
				ref={chatContainerRef}
			>
				{messages.map((msg, i) => (
					<div
						key={i}
						className={`chat-bubble ${msg.sender === 'user' ? 'user' : 'bot'}`}
					>
						{msg.text}
					</div>
				))}

				{isThinking && (
					<div className="chat-bubble bot">
						<div className="typing-indicator">
							<span className="dot"></span>
							<span className="dot"></span>
							<span className="dot"></span>
						</div>
					</div>
				)}

				{/* This invisible div acts as the scroll anchor */}
				<div ref={messagesEndRef} />
			</div>

			<div className="chat-input-group">
				<TextInput
					placeholder="Type your message..."
					value={input}
					onChange={(value) => setInput(value)}
					onKeyPress={handleKeyPress}
					isDisabled={isThinking}
					className="chat-text-input"
				/>
				<Button
					onClick={handleSend}
					isDisabled={!input.trim() || isThinking}
					className="chat-send-btn"
					variant="primary"
				>
					<FontAwesomeIcon icon={faSend} />
				</Button>
			</div>
		</div>
	);
};

export default ChatInterface;
